import { IpfError } from '../IpfError';

/**
 * Deal with state that is probably result of a bug, but application may still try to continue
 *
 * Call this when the app encounters an unexpected state that suggests that there was some earlier error
 * that may cause unexpected behavior. For example when trying to remove something that is already removed,
 * retrieving information about non-existent nodes etc - e.g. we can return "no bounding box" for a node
 * that does not exist, but it is still weird that some code asks for bounding box of non-existent node.
 *
 * This function gives us freedom in how to deal with these situations in various environments.
 * For now, we fail fast in development to detect the error early and try to continue in production.
 *
 * In future we may add some soft error to production, like "An error occured. You can try to finish your work,
 * but we recommend to refresh the page ASAP."
 *
 * Exceptions from this function should be caught by global error boundaries, so proper handling can be expected,
 * but any exception will interrupt caller code. In case we want to trigger some app behavior (like soft error
 * described above), I'd recommend to exceptionally use some global mechanism (e.g. global event to which
 * the notification mechanism listens), since we want to avoid the need of passing any dependencies to this function.
 */
export const stumble = (message: string): void => {
  // Create new error outside timeout to get proper stack
  const err = new IpfError('STUMBLE', message);
  setTimeout(() => {
    throw err;
  }, 0);
};

/**
 * Allow using stumble in functional flow, null coalescing, short-circuits etc
 *
 * @example
 * const findUser: (id: number) => User = flow(
 *   plainMap.getFrom(users),
 *   option.getOrElse(flow(
 *     createAnonymousUser(),
 *     stumbling('user not found, returning anonymous')
 *   )),
 * );
 * return findUser(id) ?? stumbling('user is null')(createAnonymousUser());
 */
export const stumbling =
  (message: string) =>
  <T,>(returnValue: T): T => {
    stumble(message);
    return returnValue;
  };
